/**
 * Core color library:
 *
 * https://www.figma.com/file/zYke5qihweC8yQSpXpcRO2/%F0%9F%93%9000-OM-CORE-Library?node-id=2%3A184
 */

import { Theme } from '../utilities/theme';

// ============================================================================
// README
// ----------------------------------------------------------------------------
// The following objects are taken directly from the CORE figma diagram.
// No additions or changes should be made here, except to align with the CORE
// diagram.
// Changes to the CORE diagram should go through the process defined in RFC009.
//
// Don't export or refer to the direct color codes, instead refer to the FUTURO
// theme and color name.
// ============================================================================

const GREYSCALE = {
  BLACK: '#000000',
  GREY_100: '#363940',
  GREY_80: '#737373',
  GREY_60: '#CCCFD3',
  GREY_40: '#DEDEDE',
  GREY_20: '#ECEEEF',
  GREY_00: '#F9F9F9',
  GRAYISH: '#A7A7A7',
  WHITE_100: '#FFFFFF',
};

const PRIMARY = {
  PRIMARY_100: '#E04B00',
  PRIMARY_80: '#FF5500',
  PRIMARY_10: '#FFD7C2',
  PRIMARY_05: '#FFE5D8',
  PRIMARY_00: '#FFF4EE',
};

const SUPPORT = {
  SUCCESS_100: '#0F6D1F',
  SUCCESS_50: '#00A31B',
  SUCCESS_00: '#EDF9F3',
  ALERT_100: '#A71D1D',
  ALERT_50: '#DD0000',
  ALERT_00: '#FFECEC',
  WARNING_100: '#876D1C',
  WARNING_00: '#FFF6DC',
  EDITABLE_100: '#3C3C6D',
  EDITABLE_00: '#ECF4FF',
  ANALYTICS_80: '#145ECB',
  FORM_FIELDS: '#D5DFE9',
};

const DARK = {
  BLACK: '#080820',
  DARK_100: '#151534',
  DARK_80: '#232354',
  DARK_60: '#3C3C6D',
  GREY_80: '#5B688C',
  GREY_60: '#9DA5BA',
  PRIMARY_100: '#C26435',
  PRIMARY_20: '#FFB996',
  FORM_FIELDS: '#424F72',
  SUCCESS_100: '#13382C',
  SUCCESS_50: '#4DA358',
  ALERT_100: '#553041',
  ALERT_50: '#FF8080',
  WARNING_100: '#4D4428',
  WARNING_00: '#C8BA8F',
  ANALYTICS_80: '#145ECB',
};

type ValueOf<T> = T[keyof T];

export type ColorCodes =
  | ValueOf<typeof GREYSCALE>
  | ValueOf<typeof PRIMARY>
  | ValueOf<typeof SUPPORT>
  | ValueOf<typeof DARK>;

export const FUTURO = {
  LIGHT: {
    /** Used for headings, form labels and body copy */
    SPACE: GREYSCALE.BLACK,
    /** Used for global navigation icons, headings, form inputs and body copy */
    STORM: GREYSCALE.GREY_100,
    /** Used for subheadings (‘Label small’), status labels, hint text in form fields, disabled input text in form fields, and disabled text buttons */
    STEEL: GREYSCALE.GREY_80,
    /** Used for disabled hint text on form fields, and disabled primary buttons */
    SHADOW: GREYSCALE.GREY_60,
    /** Used for emphasis dividers on background-regular background, and outlines (stroke) */
    SLATE: GREYSCALE.GREY_40,
    /** Used for secondary background, subtle dividers on background-card background (cards), unselected pill filters, and outlines (stroke) */
    SMOG: GREYSCALE.GREY_20,
    /** Used for disabled form fields (stroke) and status labels (stroke) */
    STONE: GREYSCALE.GREY_20,
    /** Used for regular dividers on background-regular background */
    ASH: GREYSCALE.GREY_20,
    /** Used for disabled form fields (fill) and status labels */
    SMOKE: GREYSCALE.GREY_00,
    /** For font */
    GRAYISH: GREYSCALE.GRAYISH,
    /** Used for primary background */
    SWAN: GREYSCALE.WHITE_100,
    /** Used for cards */
    SNOW: GREYSCALE.WHITE_100,
    /** Used for primary button labels */
    WHITE: GREYSCALE.WHITE_100,
    /** Used for form fields */
    SILVER: SUPPORT.FORM_FIELDS,
    /** Used for special headings (e.g. experiment slots/ads) and active tab */
    BURNT_ORANGE: PRIMARY.PRIMARY_100,
    /** Used for interactive icons and labels, text buttons, links in body copy, checkboxes and radio buttons */
    SWEET_ORANGE: PRIMARY.PRIMARY_80,
    /** Used for primary and secondary buttons */
    BUTTON_ORANGE: PRIMARY.PRIMARY_80,
    /** Used for emphasised headings */
    HEADING_BRIGHT: PRIMARY.PRIMARY_80,
    /** Used for special dividers (Primary 10 divider on a Primary 00 background) */
    PEACH: PRIMARY.PRIMARY_10,
    /** Used for emphasised background */
    BLUSH: PRIMARY.PRIMARY_05,
    /** Used for info banners and snackbars */
    LIGHT_PEACH: PRIMARY.PRIMARY_00,
    /** Used for active item highlight, and selected pill filters */
    LIGHT_CREAM: PRIMARY.PRIMARY_00,
    /** Used for emphasised background and graphic strokes */
    FULL_CREAM: PRIMARY.PRIMARY_00,
    /** Used for icons and status labels */
    EMERALD: SUPPORT.SUCCESS_100,
    /** Used for icons, status labels, and bullet points in an success banner */
    CLOVER: SUPPORT.SUCCESS_50,
    /** Used for success banners and status labels */
    MINT: SUPPORT.SUCCESS_00,
    /** Used for icons */
    RUBY: SUPPORT.ALERT_100,
    /** Used for form errors, icons, status labels, and bullet points in an alert banner */
    ROSE: SUPPORT.ALERT_50,
    /** Used for alert banners and status labels */
    BUBBLEGUM: SUPPORT.ALERT_00,
    /** Used for icons and status labels */
    GOLD: SUPPORT.WARNING_100,
    /** Used for warning banners and status labels */
    LEMON: SUPPORT.WARNING_00,
    /** Used for icons and status labels */
    SAPPHIRE: SUPPORT.EDITABLE_100,
    /** Used for status labels */
    SKY: SUPPORT.EDITABLE_00,
    /** Used for analytics */
    ELECTRIC_BLUE: SUPPORT.ANALYTICS_80,
  },
  DARK: {
    /** Used for headings, form labels and body copy */
    SPACE: GREYSCALE.GREY_20,
    /** Used for global navigation icons, headings, form inputs and body copy */
    STORM: GREYSCALE.GREY_40,
    /** Used for subheadings (‘Label small’), status labels, hint text in form fields, disabled input text in form fields, and disabled text buttons */
    STEEL: DARK.GREY_60,
    /** Used for disabled hint text on form fields, and disabled primary buttons */
    SHADOW: DARK.GREY_80,
    /** Used for emphasis dividers on background-regular background, and outlines (stroke) */
    SLATE: DARK.DARK_60,
    /** Used for secondary background, subtle dividers on background-card background (cards), unselected pill filters, and outlines (stroke) */
    SMOG: DARK.BLACK,
    /** Used for disabled form fields (stroke) and status labels (stroke) */
    STONE: DARK.DARK_80,
    /** Used for regular dividers on background-regular background */
    ASH: DARK.DARK_60,
    /** For font */
    GRAYISH: GREYSCALE.GRAYISH,
    /** Used for disabled form fields (fill) and status labels */
    SMOKE: DARK.BLACK,
    /** Used for primary background */
    SWAN: DARK.DARK_100,
    /** Used for cards */
    SNOW: DARK.DARK_80,
    /** Used for primary button labels */
    WHITE: GREYSCALE.WHITE_100,
    /** Used for form fields */
    SILVER: DARK.FORM_FIELDS,
    /** Used for special headings (e.g. experiment slots/ads) and active tab */
    BURNT_ORANGE: DARK.PRIMARY_20,
    /** Used for interactive icons and labels, text buttons, links in body copy, checkboxes and radio buttons */
    SWEET_ORANGE: DARK.PRIMARY_20,
    /** Used for primary and secondary buttons */
    BUTTON_ORANGE: DARK.PRIMARY_100,
    /** Used for emphasised headings */
    HEADING_BRIGHT: GREYSCALE.GREY_20,
    /** Used for special dividers (Primary 10 divider on a Primary 00 background) */
    PEACH: DARK.DARK_60,
    /** Used for emphasised background */
    BLUSH: DARK.DARK_80,
    /** Used for info banners and snackbars */
    LIGHT_PEACH: DARK.DARK_60,
    /** Used for active item highlight, and selected pill filters */
    LIGHT_CREAM: DARK.DARK_80,
    /** Used for emphasised background and graphic strokes */
    FULL_CREAM: DARK.BLACK,
    /** Used for icons and status labels */
    EMERALD: DARK.SUCCESS_50,
    /** Used for icons, status labels, and bullet points in an success banner */
    CLOVER: DARK.SUCCESS_50,
    /** Used for success banners and status labels */
    MINT: DARK.SUCCESS_100,
    /** Used for icons */
    RUBY: SUPPORT.ALERT_50,
    /** Used for form errors, icons, status labels, and bullet points in an alert banner */
    ROSE: DARK.ALERT_50,
    /** Used for alert banners and status labels */
    BUBBLEGUM: DARK.ALERT_100,
    /** Used for icons and status labels */
    GOLD: DARK.WARNING_00,
    /** Used for warning banners and status labels */
    LEMON: DARK.WARNING_100,
    /** Used for icons and status labels */
    SAPPHIRE: GREYSCALE.GREY_60,
    /** Used for status labels */
    SKY: DARK.FORM_FIELDS,
    /** Used for analytics */
    ELECTRIC_BLUE: SUPPORT.ANALYTICS_80,
  },
};

export const FUTURO_BY_NAME = Object.keys(FUTURO.LIGHT).reduce(
  (acc, name) => ({
    ...acc,
    [name]: { LIGHT: FUTURO.LIGHT[name], DARK: FUTURO.DARK[name] },
  }),
  {}
) as {
  [name in keyof typeof FUTURO.LIGHT]: { LIGHT: ColorCodes; DARK: ColorCodes };
};

/**
 * A small helper for cases where the dark and light theme use the same
 * colors names.
 *
 * @example
 *
 * const getStyle = (theme: Theme) => {
 *   const colors = colorsForTheme(theme);
 *   return {
 *     fontColor: colors.STORM,
 *     linkColor: colors.SWEET_ORANGE,
 *     backgroundColor: colors.SNOW,
 *   };
 * };
 *
 * // is the same thing as
 *
 * const getStyle = (theme: Theme) => {
 *   switch (theme) {
 *     case 'futuro-dark':
 *       return {
 *         fontColor: colors.DARK.STORM,
 *         linkColor: colors.DARK.SWEET_ORANGE,
 *         backgroundColor: colors.DARK.SNOW,
 *       };
 *     case 'futuro':
 *     default:
 *       return {
 *         fontColor: colors.LIGHT.STORM,
 *         linkColor: colors.LIGHT.SWEET_ORANGE,
 *         backgroundColor: colors.LIGHT.SNOW,
 *       };
 *   }
 * };
 */
export const colorsForTheme = (theme: Theme) => {
  switch (theme) {
    case 'futuro-dark':
      return FUTURO.DARK;
    case 'futuro':
    default:
      return FUTURO.LIGHT;
  }
};

const COLOR = {
  FUTURO,
};

export default COLOR;
